import React from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CrossCategoryDiscovery = ({ categories, activeCategory, projects }) => {
  const otherCategories = categories?.filter(cat => cat?.id !== activeCategory);
  
  const getFeaturedProject = (categoryId) => {
    const categoryProjects = projects?.filter(p => p?.category === categoryId);
    return categoryProjects?.[0]; // Get first project as featured
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Explore Our Full Creative Range
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover how Skycrest Studios adapts our expertise across different industries, 
            each with its own unique visual language and storytelling approach.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {otherCategories?.map((category) => {
            const featuredProject = getFeaturedProject(category?.id);
            
            return (
              <div
                key={category?.id}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-700 transform hover:scale-105"
              >
                <div className="aspect-[4/3] relative overflow-hidden">
                  {featuredProject && (
                    <Image
                      src={featuredProject?.thumbnail}
                      alt={`${category?.name} showcase`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  )}
                  <div 
                    className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
                    style={{
                      background: `linear-gradient(to top, ${category?.accentColor}CC, ${category?.accentColor}40, transparent)`
                    }}
                  />
                  
                  <div className="absolute top-4 left-4">
                    <div 
                      className="p-3 rounded-full backdrop-blur-sm"
                      style={{ backgroundColor: `${category?.accentColor}40` }}
                    >
                      <Icon 
                        name={category?.icon} 
                        size={24} 
                        color="white" 
                      />
                    </div>
                  </div>

                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {category?.name}
                    </h3>
                    <p className="text-gray-200 text-sm mb-4 line-clamp-2">
                      {category?.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <Icon name="Eye" size={16} color="white" />
                          <span className="text-white text-sm">
                            {category?.projectCount} projects
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Star" size={16} color="white" />
                          <span className="text-white text-sm">
                            {category?.rating}
                          </span>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="ArrowRight"
                        iconPosition="right"
                        className="text-white hover:bg-white/20 backdrop-blur-sm"
                      >
                        Explore
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                <Link
                  to={`/dynamic-portfolio-ecosystem?category=${category?.id}`}
                  className="absolute inset-0 z-10"
                  aria-label={`Explore ${category?.name} portfolio`}
                />
              </div>
            );
          })}
        </div>

        <div className="text-center mt-16">
          <div className="inline-flex items-center space-x-4 p-6 bg-white rounded-2xl shadow-lg">
            <div className="flex -space-x-2">
              {categories?.slice(0, 4)?.map((category) => (
                <div
                  key={category?.id}
                  className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center"
                  style={{ backgroundColor: category?.accentColor }}
                >
                  <Icon name={category?.icon} size={20} color="white" />
                </div>
              ))}
            </div>
            <div className="text-left">
              <h4 className="font-semibold text-gray-900 mb-1">
                One Studio, Infinite Possibilities
              </h4>
              <p className="text-sm text-gray-600">
                From intimate moments to bold campaigns
              </p>
            </div>
            <Link to="/services-architecture-hub">
              <Button
                variant="default"
                iconName="ArrowRight"
                iconPosition="right"
                className="bg-primary hover:bg-primary/90"
              >
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CrossCategoryDiscovery;